import { type NowPlayingSchedule, type StationSyncController } from "./station-sync.js";

/** Seek only when the element is further than this from the programmed position. */
const HOLD_SECONDS = 30;

/** After a seek, wait this long before seeking again if the element has not arrived. */
const SEEK_SETTLE_MS = 5_000;

/**
 * Wake slightly past a one-Recording wrap so the clock has left the finished pass.
 * Matches the idle display, which uses the same guard at each boundary.
 */
const WRAP_GUARD_MS = 50;

export type AutoPlayoutEvent = "tune" | "timeupdate" | "ended" | "resume";

export type AutoPlayoutVerdict =
  | { readonly type: "hold" }
  | { readonly type: "seek"; readonly index: number; readonly offsetSeconds: number }
  | { readonly type: "waitUntil"; readonly atMs: number };

export interface AutoPlayoutInput {
  event: AutoPlayoutEvent;
  /** Recording index the element is actually on. */
  playingIndex: number;
  /** Element position within that Recording, in seconds. */
  positionSeconds: number;
  schedule: NowPlayingSchedule;
  nowMs?: number;
}

const HOLD: AutoPlayoutVerdict = { type: "hold" };

interface IssuedSeek {
  index: number;
  offsetSeconds: number;
  issuedAtMs: number;
}

/**
 * Decides whether the Auto playhead holds, seeks, or waits.
 * Borrows the shared clock. Does not touch an audio element.
 */
export class AutoPlayout {
  private readonly finished = new Set<number>();
  private inFlight: IssuedSeek | null = null;
  private wrapWait: { atMs: number } | null = null;

  constructor(private readonly sync: StationSyncController) {}

  /** Drop latch, in-flight seek, and a pending wrap wait. */
  reset(): void {
    this.finished.clear();
    this.inFlight = null;
    this.wrapWait = null;
  }

  decide(input: AutoPlayoutInput): AutoPlayoutVerdict {
    const nowMs = input.nowMs ?? Date.now();
    const { schedule } = input;
    if (!schedule.available || schedule.playlist.length === 0 || schedule.totalDuration <= 0) {
      return HOLD;
    }

    if (input.event === "tune") {
      this.reset();
      const programmed = this.sync.resolvePosition(schedule);
      return this.issueSeek(programmed.index, programmed.offsetSeconds, nowMs);
    }

    if (this.wrapWait && nowMs < this.wrapWait.atMs) {
      return { type: "waitUntil", atMs: this.wrapWait.atMs };
    }
    this.wrapWait = null;

    const programmed = this.sync.resolvePosition(schedule);
    const length = schedule.playlist.length;
    const playingIndex = input.playingIndex;

    if (
      input.event !== "ended" &&
      this.seekInFlight(playingIndex, input.positionSeconds, nowMs)
    ) {
      return HOLD;
    }

    if (playingIndex < 0 || playingIndex >= length) {
      this.finished.clear();
      return this.issueSeek(programmed.index, programmed.offsetSeconds, nowMs);
    }

    if (programmed.index === playingIndex) this.finished.clear();

    const clockBehind = this.finished.has(programmed.index);
    const distance = forwardDistance(playingIndex, programmed.index, length);

    if (input.event === "ended") {
      if (programmed.index === playingIndex || clockBehind) {
        if (length === 1) return this.waitForWrap(schedule, programmed.offsetSeconds, nowMs);
        this.finished.add(playingIndex);
        return this.issueSeek(nextIndex(playingIndex, length), 0, nowMs);
      }
      this.finished.clear();
      return this.issueSeek(programmed.index, programmed.offsetSeconds, nowMs);
    }

    if (clockBehind) return HOLD;

    if (input.event === "resume") {
      this.finished.clear();
      return this.issueSeek(programmed.index, programmed.offsetSeconds, nowMs);
    }

    if (distance === 0) {
      const drift = Math.abs(input.positionSeconds - programmed.offsetSeconds);
      if (drift > HOLD_SECONDS) {
        return this.issueSeek(playingIndex, programmed.offsetSeconds, nowMs);
      }
      return HOLD;
    }

    if (distance === 1) return HOLD;

    this.finished.clear();
    return this.issueSeek(programmed.index, programmed.offsetSeconds, nowMs);
  }

  private waitForWrap(
    schedule: NowPlayingSchedule,
    offsetSeconds: number,
    nowMs: number,
  ): AutoPlayoutVerdict {
    const duration = schedule.playlist[0]?.durationSeconds ?? 0;
    const remainingMs = Math.max(0, (duration - offsetSeconds) * 1000);
    const atMs = nowMs + remainingMs + WRAP_GUARD_MS;
    this.wrapWait = { atMs };
    return { type: "waitUntil", atMs };
  }

  private seekInFlight(playingIndex: number, positionSeconds: number, nowMs: number): boolean {
    const flight = this.inFlight;
    if (!flight) return false;
    const arrived =
      playingIndex === flight.index &&
      Math.abs(positionSeconds - flight.offsetSeconds) <= HOLD_SECONDS;
    const expired = nowMs - flight.issuedAtMs >= SEEK_SETTLE_MS;
    if (arrived || expired) {
      this.inFlight = null;
      return false;
    }
    return true;
  }

  private issueSeek(index: number, offsetSeconds: number, nowMs: number): AutoPlayoutVerdict {
    this.wrapWait = null;
    this.inFlight = { index, offsetSeconds, issuedAtMs: nowMs };
    return { type: "seek", index, offsetSeconds };
  }
}

function forwardDistance(from: number, to: number, length: number): number {
  if (length <= 0) return 0;
  return (to - from + length) % length;
}

function nextIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return (index + 1) % length;
}
